import {
  memo,
  use,
  useLayoutEffect,
  useMemo,
  useState,
  createContext,
  type ReactNode,
} from 'react';
import { StoredIsColorThemeDark } from '@/shared/utils/Stored';

type ColorThemeContextValue = {
  isDark: boolean;
  switchColorTheme: () => void;
  setColorTheme: (theme: 'light' | 'dark') => void;
};

const ColorThemeContext = createContext<ColorThemeContextValue | null>(null);

export function useColorTheme() {
  const ctx = use(ColorThemeContext);
  if (ctx === null) {
    throw new Error('useColorTheme must be used within ColorThemeProvider');
  }
  return ctx;
}

export default memo(ColorThemeProvider);

function ColorThemeProvider({ children }: { children: ReactNode }) {
  const [isDark, setIsDark] = useState(StoredIsColorThemeDark.get());
  const value = useMemo<ColorThemeContextValue>(() => {
    const setColorTheme = (theme: 'light' | 'dark') => {
      const dark = theme === 'dark';
      StoredIsColorThemeDark.set(dark);
      setIsDark(dark);
    };
    return {
      isDark,
      setColorTheme,
      switchColorTheme: () => setColorTheme(isDark ? 'light' : 'dark'),
    };
  }, [isDark]);

  // A layout effect writes the attribute before the first paint; a passive
  // effect would let a dark-preference user see one light frame.
  useLayoutEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      isDark ? 'dark' : 'light',
    );
  }, [isDark]);

  return <ColorThemeContext value={value}>{children}</ColorThemeContext>;
}
